import { Link, Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { isStaffRole } from "@lms/backend/lib/auth/post-login-redirect";
import { Card, Heading, PageShell, ProgressMeter, Text } from "@/components/ui";
import { buttonClasses } from "@/components/ui/button";
import { useAuthStore } from "@/stores/authStore";
import { fetchStudentDashboard } from "@/services/api/student";
import { fetchSubscriptionPlans } from "@/services/api/subscriptions";
import { ApiDownloadButton } from "@/components/api-download-button";

const linkClass = "text-[var(--accent-ink)] underline underline-offset-4";

export function TenantDashboardPage() {
  const { tenantSlug = "" } = useParams<{ tenantSlug: string }>();
  const t = useTranslations("dashboard");
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const dashboardQuery = useQuery({
    queryKey: ["student-dashboard", tenantSlug],
    queryFn: fetchStudentDashboard,
    enabled: isAuthenticated && user?.role === "student",
  });
  // PAY-8 (lane L4): link to subscription plans only when the tenant has some.
  const plansQuery = useQuery({
    queryKey: ["subscription-plans", tenantSlug],
    queryFn: fetchSubscriptionPlans,
    enabled: isAuthenticated && user?.role === "student",
  });
  const hasPlans = (plansQuery.data?.length ?? 0) > 0;

  if (!isAuthenticated) {
    return <Navigate to={`/t/${tenantSlug}/login`} replace />;
  }

  if (isStaffRole(user?.role)) {
    return <Navigate to={`/t/${tenantSlug}/instructor`} replace />;
  }

  if (dashboardQuery.isLoading) {
    return (
      <PageShell width="app" data-testid="student-dashboard-loading">
        <Text tone="muted">{t("loading")}</Text>
      </PageShell>
    );
  }

  if (dashboardQuery.isError) {
    return (
      <PageShell width="app" data-testid="student-dashboard-error">
        <Heading as="h1" level="page">
          {t("title")}
        </Heading>
        <Text tone="muted" className="mt-2">
          {t("loadFailed")}
        </Text>
      </PageShell>
    );
  }

  const enrollments = dashboardQuery.data?.courses ?? [];
  const displayName = user?.name ?? user?.email ?? "";

  return (
    <PageShell width="app" data-testid="student-dashboard">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Heading as="h1" level="page">
            {t("title")}
          </Heading>
          <Text tone="muted" size="base" className="mt-2">
            {t("welcome", { name: displayName })}
          </Text>
        </div>
        <div className="flex flex-wrap gap-4 text-sm">
          <Link to={`/t/${tenantSlug}/courses`} className={linkClass}>
            {t("browseCatalog")}
          </Link>
          {hasPlans ? (
            <Link
              to={`/t/${tenantSlug}/subscriptions`}
              className={linkClass}
              data-testid="dashboard-subscriptions-link"
            >
              {t("viewSubscriptionPlans")}
            </Link>
          ) : null}
          <Link
            to={`/t/${tenantSlug}/settings/privacy`}
            className={linkClass}
            data-testid="dashboard-privacy-link"
          >
            {t("privacy")}
          </Link>
        </div>
      </div>

      <section className="mt-8">
        {enrollments.length === 0 ? (
          <Card tone="dashed" pad="lg" className="text-center">
            <Text tone="muted">{t("empty")}</Text>
          </Card>
        ) : (
          <ul className="space-y-4" data-testid="dashboard-enrollment-list">
            {enrollments.map((row) => (
              <li key={row.enrollmentId}>
                <Card pad="base" data-testid={`dashboard-course-${row.courseSlug}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Heading as="h2" level="card">
                          {row.courseTitle}
                        </Heading>
                        {row.status === "completed" ? (
                          <span
                            className="rounded-full bg-[var(--accent-soft)] px-2 py-0.5 text-xs font-medium text-[var(--accent-ink)]"
                            data-testid={`dashboard-completed-${row.courseSlug}`}
                          >
                            {t("courseCompleted")}
                          </span>
                        ) : null}
                      </div>
                      <Text tone="muted" size="xs" className="mt-0.5">
                        {t("enrolledOn", {
                          date: new Date(row.enrolledAt).toLocaleDateString(),
                        })}
                      </Text>
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-xs text-[var(--text-muted)]">
                          <span data-testid={`dashboard-progress-label-${row.courseSlug}`}>
                            {t("lessonsProgress", {
                              completed: row.completedLessons,
                              total: row.totalLessons,
                            })}
                          </span>
                          <span data-testid={`dashboard-progress-percent-${row.courseSlug}`}>
                            {row.progressPercent}%
                          </span>
                        </div>
                        <ProgressMeter
                          value={row.progressPercent}
                          label={t("lessonsProgress", {
                            completed: row.completedLessons,
                            total: row.totalLessons,
                          })}
                          className="mt-1"
                          data-testid={`dashboard-progress-bar-${row.courseSlug}`}
                        />
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                      <Link
                        to={`/t/${tenantSlug}/learn/${row.courseSlug}`}
                        className={buttonClasses({ variant: "accent", size: "sm" })}
                      >
                        {t("continueLearning")}
                      </Link>
                      {row.certificateId ? (
                        <ApiDownloadButton
                          path={`/v1/courses/${row.courseId}/certificate/download?as=link`}
                          fileName={`certificate-${row.courseSlug}.pdf`}
                          className={buttonClasses({ variant: "secondary", size: "sm" })}
                          data-testid={`dashboard-certificate-${row.courseSlug}`}
                        >
                          {t("downloadCertificate")}
                        </ApiDownloadButton>
                      ) : null}
                    </div>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>
    </PageShell>
  );
}
